import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { DriveFileMoveOutlined } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureFile,
  useCreateSecureCollectionMutation,
  useGetSecureBulkMoveCheckQuery,
  useGetSecureProjectCollectionsQuery,
  useGetSecureProjectsQuery,
  useMoveSecureFilesMutation,
} from "features/secureUploads/secureUploads";
import { usePermissions } from "hooks/usePermissions";
import useResponsive from "hooks/useResponsive";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";
import { formatApiError } from "utils/formatApiError";

import { MoveImpactAlerts, MoveTargetFields, NEW, ROOT } from "./moveParts";
import {
  moveConfirmLabel,
  moveRefusal,
  placesTaken,
  sourceLabel,
} from "./moveModel";

/** "collection": within the files' project; "project": into another project. */
export type MoveMode = "collection" | "project";

export type MovableFile = Pick<
  SecureFile,
  | "id"
  | "file_name"
  | "project_id"
  | "project_name"
  | "collection_id"
  | "collection_name"
>;

export interface MoveRequest {
  files: MovableFile[];
  mode: MoveMode;
  /** The menu item or button that opened it: "Add to Project", "Move File", ... */
  title: string;
}

/** Move one file or several - between the collections of their project, or into
 * another project after seeing who would lose them and who would newly see them.
 * Whoever organizes the exchange may add the people left out on the way and make
 * a collection; a client user is told to ask their Calibrate team instead. */
export default function MoveFilesDialog({
  request,
  clientId,
  onClose,
  onMoved,
}: {
  /** null: closed. */
  request: MoveRequest | null;
  clientId: number;
  onClose: () => void;
  /** After the move, before the dialog closes. */
  onMoved?: (ids: number[]) => void;
}) {
  const dispatch = useDispatch();
  const { readOnly } = useViewAs();
  const { sees } = usePermissions();
  const { isPhone } = useResponsive();
  const organizes = sees(ACTIONS.SECURE_EXCHANGE_ORGANIZE);
  const [projectChoice, setProjectChoice] = useState("");
  const [collectionChoice, setCollectionChoice] = useState(ROOT);
  const [newName, setNewName] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const open = request !== null;
  const files = useMemo(() => request?.files ?? [], [request]);
  const ids = files.map((file) => file.id);
  const sameProject = request?.mode === "collection";
  // Inside a project nothing is chosen yet: the places the files are in are left out.
  const taken = useMemo(
    () => (sameProject ? placesTaken(files) : new Set<string>()),
    [sameProject, files],
  );

  useEffect(() => {
    setProjectChoice("");
    setCollectionChoice(request?.mode === "collection" ? "" : ROOT);
    setNewName("");
    setFailure(null);
  }, [request]);

  const projectId = sameProject
    ? (files[0]?.project_id ?? null)
    : Number(projectChoice) || null;
  const { data: openProjects } = useGetSecureProjectsQuery(
    { client_id: clientId, status: "open", page_size: 200 },
    { skip: !open || sameProject },
  );
  const { data: collections = [] } = useGetSecureProjectCollectionsQuery(
    projectId ?? 0,
    { skip: !open || !projectId },
  );
  const collectionId =
    collectionChoice === ROOT || collectionChoice === NEW || !collectionChoice
      ? null
      : Number(collectionChoice);
  // currentData: a check that failed for this target must not leave the last
  // target's people on screen, or the move would add people nobody was shown.
  // Inside one project nothing changes for anyone, so nothing is checked.
  const {
    currentData: check,
    isFetching: checking,
    error: checkError,
    refetch: checkAgain,
  } = useGetSecureBulkMoveCheckQuery(
    { ids, project_id: projectId ?? 0, collection_id: collectionId },
    { skip: !open || sameProject || !projectId || ids.length === 0 },
  );
  const [move, { isLoading: moving }] = useMoveSecureFilesMutation();
  const [createCollection, { isLoading: creating }] =
    useCreateSecureCollectionMutation();
  const busy = moving || creating;

  if (!request) return null;

  const several = files.length > 1;
  const sources = new Set(files.map((file) => file.project_id));
  const targets = (openProjects?.results ?? []).filter(
    (project) => !sources.has(project.id),
  );
  const losing = check?.losing ?? [];
  const gaining = check?.gaining ?? [];
  const projectName =
    targets.find((project) => project.id === projectId)?.name ?? "the project";
  const ready =
    Boolean(projectId) &&
    collectionChoice !== "" &&
    !(collectionChoice === NEW && !newName.trim()) &&
    (sameProject ||
      (!checking &&
        !checkError &&
        check !== undefined &&
        (organizes || !losing.length)));
  const checkFailure = checkError
    ? (checkError as { status?: number }).status === 404
      ? several
        ? "Some of these files are no longer here. Close this and select them again."
        : "This file is no longer here."
      : formatApiError(
          (checkError as { data?: unknown }).data,
          "The move could not be checked.",
        )
    : null;

  const submit = async () => {
    if (!projectId) return;
    setFailure(null);
    try {
      let target = collectionId;
      if (collectionChoice === NEW) {
        const made = await createCollection({
          projectId,
          name: newName.trim(),
        }).unwrap();
        target = made.id;
        // A retry after a refused move goes into it instead of making it again.
        setCollectionChoice(String(made.id));
        setNewName("");
      }
      const done = await move({
        ids,
        project_id: projectId,
        collection_id: target,
        add_members: organizes && losing.length > 0,
      }).unwrap();
      dispatch(
        pushNotification({
          message:
            done.moved === 1
              ? `File moved to ${done.to}.`
              : `${done.moved} files moved to ${done.to}.`,
          severity: "success",
        }),
      );
      onMoved?.(ids);
      onClose();
    } catch (error) {
      setFailure(
        moveRefusal(
          (error as { data?: unknown })?.data,
          several
            ? "The files could not be moved."
            : "The file could not be moved.",
        ),
      );
      // Who would lose them may have changed since the check: ask again.
      if (!sameProject) void checkAgain();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{ sx: { borderRadius: isPhone ? 0 : 2, overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <FormHeader
          avatar={<DriveFileMoveOutlined fontSize="small" />}
          title={request.title}
          onClose={onClose}
          closeDisabled={busy}
        />
        <Box sx={{ p: 3, display: "grid", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <b>{several ? `${files.length} files` : files[0]?.file_name}</b>
            {` - from ${sourceLabel(files)}`}
          </Typography>
          <MoveTargetFields
            projects={sameProject ? null : targets}
            projectChoice={projectChoice}
            onProject={(value) => {
              setProjectChoice(value);
              setCollectionChoice(ROOT);
            }}
            projectId={projectId}
            collections={collections}
            collectionChoice={collectionChoice}
            onCollection={setCollectionChoice}
            newName={newName}
            onNewName={setNewName}
            disabled={busy}
            hidden={taken}
            allowNew={organizes}
          />
          <MoveImpactAlerts
            losing={losing}
            gaining={gaining}
            projectName={projectName}
            files={files.length}
            canAdd={organizes}
          />
          {failure || checkFailure ? (
            <Alert severity="error">{failure ?? checkFailure}</Alert>
          ) : null}
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={onClose}
        cancelDisabled={busy}
      >
        <Button
          variant="contained"
          size="small"
          onClick={submit}
          disabled={!ready || busy || readOnly}
          sx={FOOTER_BUTTON_SX}
        >
          {busy
            ? "Moving..."
            : moveConfirmLabel(organizes ? losing : [], files.length)}
        </Button>
      </FormFooter>
    </Dialog>
  );
}
